import PropTypes from 'prop-types';
import cls from 'classnames';

import { DynamicAsset } from '@/molecules/Media/index';
import RichText from '@/molecules/RichText/RichText';
import CtfCta from '@/molecules/CTA/index';
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

import { CtfAssetPositions, CtfAssetOrientations, CtfTwoColumnGap, CtfBackgroundColor, CtfAdditionalTitleAndTextSize, CtfAdditionalTitleAndTextPosition } from '@/utils/constants';
import Text from '@/atoms/Text/Text';

function StoryTwoColumns({ entries = [], fields = {} }) {
  const {
    id,
    title,
    additionalTitle,
    text,
    additionalText,
    callToAction,
    superText,
    cloudinaryAssets,
    assetPosition,
    assetOrientation,
    twoColumnGap,
    backgroundColor,
    headingWeight,
    hideHeader,
    additionalTitleAndTextSize,
    additionalTitleAndTextPosition,
  } = fields;

  return (
    <div
      className={cls('aaco-story aaco-story-two-columns py-10 md:py-[50px]', {
        [`bg-${backgroundColor}`]: backgroundColor !== undefined,
      })}
      id={id}
    >
      <div className={cls(
        'container flex flex-col md:flex-row gap-5 mx-auto', {
          "md:gap-[170px]": twoColumnGap === CtfTwoColumnGap.WIDE,
          "md:gap-10": twoColumnGap === CtfTwoColumnGap.NARROW,
        }
      )}>
        <div
          className={cls('w-full', {
            'md:order-1': assetPosition === CtfAssetPositions.LEFT,
            'md:order-2': assetPosition === CtfAssetPositions.RIGHT,
          })}
        >
          {(cloudinaryAssets) && (
            <DynamicAsset
              assets={cloudinaryAssets}
              entries={entries}
              field={cloudinaryAssets}
              container="container"
              style={cls(
                "aspect-[167/209] md:aspect-[300/157]", {
                  "md:aspect-[300/157]": assetOrientation === CtfAssetOrientations.LANDSCAPE,
                  "md:aspect-[244/137]": assetOrientation === CtfAssetOrientations.LANDSCAPE_SMALL,
                  "md:aspect-[488/611]": assetOrientation === CtfAssetOrientations.PORTRAIT,
                }
              )}
            />
          )}
        </div>
        <div className={cls("flex flex-col w-full", {
          'md:order-1': assetPosition === CtfAssetPositions.RIGHT,
          'md:order-2': assetPosition === CtfAssetPositions.LEFT,
        })}>
          {superText && (
            <h6
              className={`mb-4 text-tag text- uppercase !m-0 font-pro-mono ${
                backgroundColor === CtfBackgroundColor.DARK_WARM_GREY
                  ? 'text-white'
                  : 'text-black'
                }
              `}
            >
              {superText}
            </h6>
          )}
          {title && !hideHeader && (
            <>
              <Text
                id={title}
                tag={headingWeight.toLowerCase()}
                style={backgroundColor === CtfBackgroundColor.DARK_WARM_GREY
                  ? "text-white"
                  : "text-black"}
              >
                {title}
              </Text>
              <hr className={cls('my-[25px] md:my-5', {
                'border-white': backgroundColor === CtfBackgroundColor.DARK_WARM_GREY,
                'border-black': backgroundColor !== CtfBackgroundColor.DARK_WARM_GREY,
              })} />
            </>
          )}
          {text && !text.content && (
            <Text
              tag="h6"
              style={backgroundColor === CtfBackgroundColor.DARK_WARM_GREY
                ? "text-white"
                : "text-black"}
            >
              {text}
            </Text>
          )}
          {text &&
            text.content &&
            text.content.length > 0 &&
            text.content.map(({ nodeType, content }, index) => (
              <RichText
                entries={entries}
                nodeType={nodeType}
                content={content}
                key={index}
                richTextStyle={{
                  paragraph: {
                    customFont:
                      TEXT_BASE_CLASS['body'] +
                      (backgroundColor === CtfBackgroundColor.DARK_WARM_GREY
                        ? ' text-white'
                        : ' text-black') +
                        ' font-heading !text-h6-mobile !font-h6-mobile md:font-h4 md:text-h4'
                  },
                }}
              />
            ))}
          <div className={cls({
            "mt-auto": additionalTitleAndTextPosition === CtfAdditionalTitleAndTextPosition.BOTTOM,
          })}>
            {additionalTitle && (
              <>
                <Text
                  id={additionalTitle}
                  tag={"h5"}
                  style={cls("text-black mt-10", {
                    "!text-white": backgroundColor === CtfBackgroundColor.DARK_WARM_GREY,
                    "max-w-[380px]": additionalTitleAndTextSize === CtfAdditionalTitleAndTextSize.SMALL,
                  })}
                >
                  {additionalTitle}
                </Text>
                <hr className={cls('my-[25px] md:my-5', {
                  'border-white': backgroundColor === CtfBackgroundColor.DARK_WARM_GREY,
                  'border-black': backgroundColor !== CtfBackgroundColor.DARK_WARM_GREY,
                  "max-w-[380px]": additionalTitleAndTextSize === CtfAdditionalTitleAndTextSize.SMALL,
                })} />
              </>
            )}
            {additionalText && !additionalText.content && (
              <div className={cls({
                "max-w-[380px]": additionalTitleAndTextSize === CtfAdditionalTitleAndTextSize.SMALL,
              })}>
                <Text
                  tag="h6"
                  style={backgroundColor === CtfBackgroundColor.DARK_WARM_GREY
                    ? "text-white"
                    : "text-black"}
                >
                  {text}
                </Text>
              </div>
            )}
            {additionalText &&
              additionalText.content &&
              additionalText.content.length > 0 && (
                <div className={cls({
                  "max-w-[380px]": additionalTitleAndTextSize === CtfAdditionalTitleAndTextSize.SMALL,
                })}>
                  {additionalText.content.map(({ nodeType, content }, index) => (
                    <RichText
                      entries={entries}
                      nodeType={nodeType}
                      content={content}
                      key={index}
                      richTextStyle={{
                        paragraph: {
                          customFont:
                            TEXT_BASE_CLASS['body'] +
                            (backgroundColor === CtfBackgroundColor.DARK_WARM_GREY
                              ? ' text-white'
                              : ' text-black')
                        },
                        list: {
                          ul: backgroundColor === CtfBackgroundColor.DARK_WARM_GREY ? "text-white" : "text-black",
                          paragraph: {
                            customFont:
                              TEXT_BASE_CLASS['caption'] +
                              (backgroundColor === CtfBackgroundColor.DARK_WARM_GREY
                                ? ' !text-white'
                                : ' text-black')
                          },
                        }
                      }}
                    />
                  ))}
                </div>
              )}
          </div>
          {callToAction && (
            <div className="product-kind-cta mt-5 md:mt-10">
              <CtfCta
                field={callToAction}
                entries={entries}
                onClick={(e, { localUrl, url, name }) =>
                  analytics({
                    window,
                    document,
                    detail: getClickCtaDetail(e, { localUrl, url, name }),
                  })
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

StoryTwoColumns.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
  hideHeader: PropTypes.bool,
};

export default StoryTwoColumns;
